import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { CursorGlow } from './components/common/CursorGlow'
import { LoadingScreen } from './components/common/LoadingScreen'
import { ScrollToTop } from './components/common/ScrollToTop'
import { ProtectedRoute } from './components/admin/ProtectedRoute'
import { PageShell } from './components/layout/PageShell'

const HomePage = lazy(() => import('./pages/HomePage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const ServicesPage = lazy(() => import('./pages/ServicesPage'))
const AnalysesPage = lazy(() => import('./pages/AnalysesPage'))
const ResearchPage = lazy(() => import('./pages/ResearchPage'))
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'))
const MediaPage = lazy(() => import('./pages/MediaPage'))
const LifestylePage = lazy(() => import('./pages/LifestylePage'))
const ReferencesPage = lazy(() => import('./pages/ReferencesPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))

function PublicRoutes() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()

  return (
    <PageShell>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          className={location.pathname === '/' ? undefined : 'subpage-flow'}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          style={{ willChange: reduceMotion ? 'auto' : 'opacity, transform' }}
        >
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/hakkimda" element={<AboutPage />} />
            <Route path="/hizmetler" element={<ServicesPage />} />
            <Route path="/analizler" element={<AnalysesPage />} />
            <Route path="/arastirmalar" element={<ResearchPage />} />
            <Route path="/arastirmalar/projeler/:slug" element={<ProjectDetailPage />} />
            <Route path="/medya" element={<MediaPage />} />
            <Route path="/yasam-stili" element={<LifestylePage />} />
            <Route path="/referanslar" element={<ReferencesPage />} />
            <Route path="/iletisim" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </PageShell>
  )
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
    </Routes>
  )
}

export function App() {
  const location = useLocation()
  const isAdminPath = location.pathname.startsWith('/admin')

  return (
    <>
      <ScrollToTop />
      <CursorGlow />
      <Suspense fallback={<div className="pt-24"><LoadingScreen /></div>}>
        {isAdminPath ? <AdminRoutes /> : <PublicRoutes />}
      </Suspense>
    </>
  )
}
